import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { first, takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as moment from 'moment';

import { TaskService } from '@app/_components/task';
import { AlertService, AuthenticationService } from '@app/_services';
import { TaskComponent } from '@app/_components/task/task/task.component';

/** Guest-portal request types as defined in the backend enum */
const GUEST_REQUEST_TYPES: { value: number; label: string; icon: string; color: string }[] = [
  { value: 200, label: 'Housekeeping – Linens',  icon: 'pi pi-tag',       color: '#6366f1' },
  { value: 201, label: 'Housekeeping – Clean',   icon: 'pi pi-home',      color: '#8b5cf6' },
  { value: 202, label: 'Maintenance',            icon: 'pi pi-wrench',    color: '#f59e0b' },
  { value: 203, label: 'Extras',                 icon: 'pi pi-gift',      color: '#10b981' },
  { value: 204, label: 'Concierge',              icon: 'pi pi-star',      color: '#0ea5e9' },
];

/** taskSource value for the guest portal */
const TASK_SOURCE_GUEST_PORTAL = 7;

@Component({
  selector: 'app-guest-request-list',
  templateUrl: './guest-request-list.component.html',
  styleUrls: ['./guest-request-list.component.css'],
  providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
  standalone: false
})
export class GuestRequestListComponent implements OnInit, OnDestroy {

  @ViewChild('pt') table: Table;

  // ── data ───────────────────────────────────────────────────────────────────
  requests: any[] = [];
  recordTotal = 0;
  loading = true;

  // ── paging / sorting ───────────────────────────────────────────────────────
  lazyLoadEvent: LazyLoadEvent;
  multiSortMeta: any[] = [];

  // ── summary counts ─────────────────────────────────────────────────────────
  summaryByType:   { label: string; icon: string; color: string; count: number }[] = [];
  summaryByStatus: { label: string; color: string; count: number }[] = [];
  totalCount = 0;

  // ── filter models ──────────────────────────────────────────────────────────
  filterTypes:   number[] = [];
  filterStatuses: number[] = [];
  filterDateFrom: Date | null = null;
  filterDateTo:   Date | null = null;

  // ── lookup tables ──────────────────────────────────────────────────────────
  readonly requestTypes = GUEST_REQUEST_TYPES;
  readonly statuses: { value: number; name: string }[];

  readonly requestTypeOptions = GUEST_REQUEST_TYPES.map(t => ({ label: t.label, value: t.value }));
  statusOptions: { label: string; value: number }[] = [];

  // ── column definitions ─────────────────────────────────────────────────────
  columns = [
    { field: 'taskId',                    header: 'ID',           width: '70px'  },
    { field: 'taskSourceId',              header: 'Reservation',  width: '110px' },
    { field: 'taskType',                  header: 'Request Type', width: '180px' },
    { field: 'status',                    header: 'Status',       width: '140px' },
    { field: 'name',                      header: 'Summary',      width: '200px' },
    { field: 'description',               header: 'Details',      width: '250px' },
    { field: 'assignedToUserVO.lastName', header: 'Assigned To',  width: '130px' },
    { field: 'dateCreated',               header: 'Submitted',    width: '120px' },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private taskService: TaskService,
    private alertService: AlertService,
    private messageService: MessageService,
    private dialog: MatDialog,
  ) {
    this.statuses = this.taskService.statuses;
    this.statusOptions = this.taskService.statuses.map(s => ({ label: s.name, value: s.value }));
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Lifecycle
  // ══════════════════════════════════════════════════════════════════════════

  ngOnInit(): void {
    this.multiSortMeta = [{ field: 'dateCreated', order: -1 }];
    this.buildEmptySummary();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Data loading
  // ══════════════════════════════════════════════════════════════════════════

  loadLazy(event: LazyLoadEvent): void {
    this.loading = true;
    this.lazyLoadEvent = event;

    if (!event.filters) { event.filters = {}; }

    // ── always lock to guest-portal source ───────────────────────────────────
    event.filters['taskSource'] = { value: TASK_SOURCE_GUEST_PORTAL, matchMode: 'in' };

    // ── default sort ──────────────────────────────────────────────────────────
    if (!event.multiSortMeta || event.multiSortMeta.length === 0) {
      event.multiSortMeta = [{ field: 'dateCreated', order: -1 }];
    }

    // ── user-driven filters ───────────────────────────────────────────────────
    if (this.filterTypes.length > 0) {
      event.filters['taskType'] = { value: this.filterTypes, matchMode: 'in' };
    } else {
      delete event.filters['taskType'];
    }

    if (this.filterStatuses.length > 0) {
      event.filters['status'] = { value: this.filterStatuses, matchMode: 'in' };
    } else {
      delete event.filters['status'];
    }

    if (this.filterDateFrom) {
      event.filters['dateCreatedFrom'] = { value: moment(this.filterDateFrom).format('YYYY-MM-DD'), matchMode: 'gte' };
    } else {
      delete event.filters['dateCreatedFrom'];
    }

    if (this.filterDateTo) {
      event.filters['dateCreatedTo'] = { value: moment(this.filterDateTo).format('YYYY-MM-DD'), matchMode: 'lte' };
    } else {
      delete event.filters['dateCreatedTo'];
    }

    const page = Math.round((event.first || 0) / (event.rows || 25));

    this.taskService.findByFilter(
      page, event.rows, event.sortField, event.sortOrder,
      event.multiSortMeta, event.globalFilter, event.filters
    ).pipe(first(), takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.requests = res?.data?.users || [];
        this.recordTotal = res?.page?.recordTotal || 0;
        this.buildSummary(this.requests);
      },
      error: (err: any) => {
        this.alertService.error(err);
        this.loading = false;
      },
      complete: () => { this.loading = false; }
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Summary helpers
  // ══════════════════════════════════════════════════════════════════════════

  private buildEmptySummary(): void {
    this.summaryByType = GUEST_REQUEST_TYPES.map(t => ({
      label: t.label, icon: t.icon, color: t.color, count: 0
    }));
    this.summaryByStatus = this.taskService.statuses.map(s => ({
      label: s.name, color: this.statusColor(s.value), count: 0
    }));
  }

  private buildSummary(rows: any[]): void {
    const typeMap: Record<number, number> = {};
    const statusMap: Record<number, number> = {};

    rows.forEach(r => {
      typeMap[r.taskType]  = (typeMap[r.taskType]  || 0) + 1;
      statusMap[r.status]  = (statusMap[r.status]  || 0) + 1;
    });

    this.summaryByType = GUEST_REQUEST_TYPES.map(t => ({
      label: t.label, icon: t.icon, color: t.color,
      count: typeMap[t.value] || 0
    }));

    this.summaryByStatus = this.taskService.statuses.map(s => ({
      label: s.name, color: this.statusColor(s.value),
      count: statusMap[s.value] || 0
    })).filter(s => s.count > 0);

    this.totalCount = rows.length;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Row actions
  // ══════════════════════════════════════════════════════════════════════════

  openRequest(task: any): void {
    const config = new MatDialogConfig();
    config.disableClose = false;
    config.autoFocus = true;
    config.hasBackdrop = true;
    config.maxWidth = '80vw';
    config.width = '60vw';
    config.data = { task };

    const ref = this.dialog.open(TaskComponent, config);

    ref.componentInstance.doUpdate.subscribe((updated: any) => {
      const idx = this.requests.findIndex(r => r.taskId === updated.taskId);
      if (idx >= 0) { this.requests[idx] = updated; this.requests = [...this.requests]; }
      this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Request #' + updated.taskId + ' saved.' });
      ref.close();
    });
  }

  updateStatus(task: any, newStatus: number, event: Event): void {
    event.stopPropagation();
    const payload = { ...task, status: newStatus };
    this.taskService.update(payload).pipe(first()).subscribe({
      next: (res: any) => {
        task.status = newStatus;
        this.requests = [...this.requests];
        this.messageService.add({ severity: 'success', summary: 'Status updated', detail: 'Request #' + task.taskId });
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Update failed', detail: JSON.stringify(err) });
      }
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Filter helpers
  // ══════════════════════════════════════════════════════════════════════════

  applyFilters(): void {
    if (this.lazyLoadEvent) {
      this.lazyLoadEvent.first = 0;
      this.loadLazy(this.lazyLoadEvent);
    } else {
      this.table?.reset();
    }
  }

  clearFilters(): void {
    this.filterTypes   = [];
    this.filterStatuses = [];
    this.filterDateFrom = null;
    this.filterDateTo   = null;
    this.table?.reset();
  }

  onDateFilter(): void {
    this.applyFilters();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Display helpers
  // ══════════════════════════════════════════════════════════════════════════

  getRequestTypeLabel(taskType: number): string {
    return GUEST_REQUEST_TYPES.find(t => t.value === taskType)?.label ?? ('Type ' + taskType);
  }

  getRequestTypeColor(taskType: number): string {
    return GUEST_REQUEST_TYPES.find(t => t.value === taskType)?.color ?? '#64748b';
  }

  getRequestTypeIcon(taskType: number): string {
    return GUEST_REQUEST_TYPES.find(t => t.value === taskType)?.icon ?? 'pi pi-circle';
  }

  getStatusLabel(status: number): string {
    return this.taskService.statuses.find(s => s.value === status)?.name ?? ('Status ' + status);
  }

  statusColor(status: number): string {
    const map: Record<number, string> = {
      0: '#22c55e',  // Complete – green
      1: '#3b82f6',  // In Progress – blue
      2: '#94a3b8',  // Not started – grey
      3: '#f59e0b',  // On Hold – amber
      4: '#ef4444',  // Cancelled – red
      5: '#a855f7',  // Ready to Review – purple
      6: '#0ea5e9',  // Ready for Prod – sky
      7: '#f97316',  // Reopened – orange
    };
    return map[status] ?? '#94a3b8';
  }

  statusBgColor(status: number): string {
    const map: Record<number, string> = {
      0: '#dcfce7',
      1: '#dbeafe',
      2: '#f1f5f9',
      3: '#fef3c7',
      4: '#fee2e2',
      5: '#f3e8ff',
      6: '#e0f2fe',
      7: '#ffedd5',
    };
    return map[status] ?? '#f1f5f9';
  }

  truncate(text: string, length = 60): string {
    if (!text) return '';
    return text.length > length ? text.slice(0, length) + '…' : text;
  }
}
